import React, { useState, useMemo } from "react";
import { Map } from "immutable";
import cx from "classnames";
import { Button, Box, Input, TextArea, Menu, MenuItem } from "@showitinc/showit-wc";

import { getTargetArea, getDropIndexOffset } from "../../../../utils/DnDUtils";
import AssetUtils from "../../../../utils/AssetUtils";
import { TITLE, DESC } from "./GalleryImageConstants";
import { createInitialDetails, updateDetailsState, prepareUpdates } from "./GalleryItemUtils";
import styles from "./LargeImageListItem.module.css";

export const imageListPanelDrop =
  ({ dropRef, index }) =>
  (dragItem, monitor) => {
    const { index: dragIndex } = dragItem;
    const area = getTargetArea(monitor, dropRef);
    const itemOffset = getDropIndexOffset({ area, dragIndex, dropIndex: index });
    const targetIndex = index + itemOffset;

    const data = {
      item: dragItem,
      target: { index: targetIndex, dropArea: area },
    };

    return { area, data };
  };

const LargeImageListItem = (props) => {
  const {
    image: asset = Map(),
    disabled,
    onEdit,
    index,
    onPropUpdate,
    removeImage,
    showButtons = false,
  } = props;
  const previewURL = AssetUtils.getV2PreviewURL(asset.get("key"), {
    provider: asset.get("provider"),
  });
  const original = useMemo(() => createInitialDetails(asset), [asset]);

  const [details, setDetails] = useState({
    ...original,
    touched: false,
  });

  const updateDetails = (key) => (e) => {
    try {
      const newValue = e.detail.value;
      setDetails((prevDetails) => updateDetailsState(prevDetails, original, key, newValue));
      onEdit && onEdit(key, { [key]: newValue });
    } catch (error) {
      console.error(`Error updating ${key} details state: `, error);
    }
  };

  const commitChanges = () => {
    try {
      const { touched, ...rest } = details;
      if (touched) {
        const preppedUpdates = prepareUpdates(index, rest, original);
        preppedUpdates.length &&
          preppedUpdates.forEach((singleUpdate) => onPropUpdate(singleUpdate));
      }
    } catch (error) {
      console.error(`Error committing changes after onBlur event: `, error);
    }
  };

  const _removeThumb = () => {
    removeImage && !disabled && removeImage(index);
  };

  return (
    <div className={styles["gallery-list-item-large"]}>
      <Box alignItems="flex-start" pr="3">
        <div
          className={styles["large-thumbnail-container"]}
          style={{ backgroundImage: `url(${previewURL})` }}
        />
        <Menu shift="left" className={styles.menu}>
          <Button
            data-testid={`large-image-list-menu-${index}`}
            variant="icon"
            icon-prefix="menuMoreVert"
            slot="trigger-element"
            iconSize="20"
            size="sm"
          />
          <MenuItem data-testid={`large-image-list-remove-${index}`} onClick={_removeThumb}>
            <Box className={styles.delete}>Remove</Box>
          </MenuItem>
        </Menu>
      </Box>

      <Box p="3">
        <Input
          data-testid={`large-image-list-title-${index}`}
          label="Title"
          value={details.title || ""}
          onSIInput={updateDetails(TITLE)}
          emitOnBlur
          onSIBlur={commitChanges}
          disabled={disabled}
          className={styles["gallery-title-container"]}
        />
        <TextArea
          data-testid={`large-image-list-alt-${index}`}
          label="Alt Text"
          value={details.desc || ""}
          onSIInput={updateDetails(DESC)}
          emitOnBlur
          onSIBlur={commitChanges}
          disabled={disabled}
          className={styles["gallery-alt-container"]}
          rows={3}
        />
      </Box>
    </div>
  );
};

export default LargeImageListItem;
